import { all, put, select, takeLatest } from 'redux-saga/effects';
import { AppointmentActionTypes } from './appointment-action';
import { AlertActionType } from '@store/alert/alert-action';
import { appointmentStore } from './appointment-store';
import getServiceDate from '@api/appointment/service-date';
import getServiceFee from '@api/appointment/service-fee';
import getServiceTimesheet from '@api/appointment/service-timesheet';
import storeService from '@api/appointment/service-store-appointment';
import { FinancialActionTypes } from '@store/financials/financials-actions';
import { homeStore } from '@store/home-screen/home-store';
import getParseRoute from '@utils/helpers/parse-route';

function* getUserServiceFeeWatcher() {
  const { selectedAddress, userId } = yield select(appointmentStore);

  try {
    const response: { data: Array<object> } = yield getServiceFee({
      userId: userId,
      subTypeId: '1',
      contactId: selectedAddress,
      cache: 'no-cache',
    });

    yield put({
      type: AppointmentActionTypes.SET_SERVICE_FEE,
      data: {
        serviceFeeList: response?.data,
      },
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
  }
}

function* getUserServiceDateWatcher() {
  const { selectedAddress, userId, selectedServiceFee } = yield select(appointmentStore);

  try {
    const response: { data: { days_list: Array<string>, first_day: string, datepicker_from: string, datepicker_to: string } } = yield getServiceDate({
      userId: userId,
      serviceId: selectedServiceFee.route_key_affix,
      locationId: selectedAddress,
      cache: 'no-cache',
    });

    if (response?.data) {
      yield put({
        type: AppointmentActionTypes.SET_APPOINTMENT_DATE,
        data: {
          availableDate: response?.data,
          selectedDate: response?.data?.first_day,
          startDate: response?.data?.datepicker_from,
          endDate: response?.data?.datepicker_to,
        },
      });

      const timesheetResponse: { data: { am: Array<object>, pm: Array<object> }, extra: { appointment_visit: Array<{ day_date_encrypt: string }> } } = yield getServiceTimesheet({
        userId: userId,
        serviceId: selectedServiceFee.route_key_affix,
        selectedDate: response?.data?.first_day,
        locationId: selectedAddress,
        cache: 'no-cache',
      });

      yield put({
        type: AppointmentActionTypes.SET_APPOINTMENT_TIMESHEET,
        data: {
          serviceTimeSheetAM: timesheetResponse?.data?.am,
          serviceTimeSheetPM: timesheetResponse?.data?.pm,
          dayDateEncrypt: timesheetResponse?.extra?.appointment_visit[0]?.day_date_encrypt,
        },
      });
    } else {
      yield put({ type: AppointmentActionTypes.SET_TIMESHEET_LOADING });
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: 'در حال حاضر، پزشک مربوطه، روز و ساعتی را برای ویزیت حضوری تعیین نکرده است، جهت اطلاعات بیشتر، می توانید با پشتیبانی تماس بگیرید.',
        severity: 'danger',
      });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
  }
}

function* getUserServiceTimeWatcher() {
  const { selectedAddress, userId, selectedServiceFee, selectedDate } = yield select(appointmentStore);

  try {
    const timesheetResponse: { data: { am: Array<object>, pm: Array<object> }, extra: { appointment_visit: Array<{ day_date_encrypt: string }> } } = yield getServiceTimesheet({
      userId: userId,
      serviceId: selectedServiceFee.route_key_affix,
      selectedDate: selectedDate,
      locationId: selectedAddress,
      cache: 'no-cache',
    });

    yield put({
      type: AppointmentActionTypes.SET_APPOINTMENT_TIMESHEET,
      data: {
        serviceTimeSheetAM: timesheetResponse?.data?.am,
        serviceTimeSheetPM: timesheetResponse?.data?.pm,
        dayDateEncrypt: timesheetResponse?.extra?.appointment_visit[0]?.day_date_encrypt,
      },
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
  }
}

function* storeServiceAppointmentWatcher() {
  const { selectedAddress, userId, selectedServiceFee, dayDateEncrypt, selectedTime } = yield select(appointmentStore);
  const { navigation } = yield select(homeStore);

  try {
    const timesheetResponse: { data: { route_key_affix: string } } = yield storeService({
      userId: userId,
      dayDateEncrypt: dayDateEncrypt,
      SelectedTime: selectedTime,
      contactId: selectedAddress,
      subTypeId: selectedServiceFee.route_key_affix,
      cache: 'no-cache',
    });

    yield put({
      type: FinancialActionTypes.SET_ORDER_ID,
      data: {
        orderItemId: timesheetResponse?.data?.route_key_affix,
      },
    });

    yield navigation.replace(getParseRoute({ pathname: `/financials/order/${timesheetResponse?.data?.route_key_affix}` }));

    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: 'سفارش شما با موفقیت ثبت شد',
      severity: 'success',
    });
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات شما مشکلی پیش آمده است، لطفا بعدا مراجعه نمایید',
      severity: 'danger',
    });
  }
}

function* appointmentMiddleware() {
  yield all([
    takeLatest(AppointmentActionTypes.GET_SERVICE_FEE, getUserServiceFeeWatcher),
    takeLatest(AppointmentActionTypes.SET_DATE_PICKER, getUserServiceDateWatcher),
    takeLatest(AppointmentActionTypes.GET_APPOINTMENT_TIMESHEET, getUserServiceTimeWatcher),
    takeLatest(AppointmentActionTypes.STORE_SERVICE_REQUEST, storeServiceAppointmentWatcher),
  ]);
}

export default appointmentMiddleware;
